import { useEffect, useState, useRef, useCallback } from "react";
import configpath from "../config";

const BASE_URL = configpath.apiUrl;
const HEALTH_ENDPOINT = `${BASE_URL}/health/`;
const ONLINE_INTERVAL = 30000;  // 30s
const OFFLINE_INTERVAL = 5000;  // 5s

function useNetworkStatus(onStatusChange) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const intervalRef = useRef(null);
  const isMounted = useRef(false);
  const onStatusChangeRef = useRef(onStatusChange);

  // Keep ref in sync so callback never causes re-renders
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const checkBackend = useCallback(async () => {
    try {
      const res = await fetch(HEALTH_ENDPOINT, {
        method: "GET",
        cache: "no-store",
        signal: AbortSignal.timeout(4000),
      });
      setIsOnline(res.ok);
    } catch {
      setIsOnline(false);
    }
  }, []);

  const startPolling = useCallback(
    (online) => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const interval = online ? ONLINE_INTERVAL : OFFLINE_INTERVAL;
      intervalRef.current = setInterval(checkBackend, interval);
    },
    [checkBackend]
  );

  useEffect(() => {
    checkBackend();

    const handleOnline = () => {
      checkBackend();
      startPolling(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
      startPolling(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    startPolling(navigator.onLine);

    return () => {
      isMounted.current = false; // reset so StrictMode remount skips the guard correctly
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkBackend, startPolling]);

  // Fire onStatusChange on every transition (skip initial mount)
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (onStatusChangeRef.current) {
      onStatusChangeRef.current(isOnline);
    }
    startPolling(isOnline);
  }, [isOnline, startPolling]);

  return isOnline;
}

export default useNetworkStatus;
